import React, { useCallback, useState } from "react";

import { useGetVerificationEditingRequests, useTranslation } from "utils";

import { EditingRequestType, editingRequestIdentifierMap } from "models";

import { AlertBanner, Button, Form, TextArea, Typography } from "components";

export type VerificationEditingRequestProps = {
  type: EditingRequestType;
  farmId: string;
  id: string;
  requestId?: string;
  onSubmitComment?: (comment: string) => Promise<void>;
};

export const EditingRequestBanner = ({ type, farmId, id, requestId = undefined, onSubmitComment = undefined }: VerificationEditingRequestProps) => {
  const { t } = useTranslation(["tasks"]);
  const [isCommentFieldVisible, setIsCommentFieldVisible] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const form = Form.useFormInstance();

  const { data: editingRequests, isLoading } = useGetVerificationEditingRequests({ farmId });

  // Only one editing request per type will be displayed.
  const key = editingRequestIdentifierMap[type];

  const activeEditingRequest = editingRequests?.find((r) => {
    const matchesId = id ? r[key] === id : r[key] === null || r[key] === "";
    const matchesType = r.requestType === type;
    const isNotCompleted = r.completedAt === null;
    const matchesRequestId = requestId ? r.id === requestId : true;

    return matchesId && matchesType && isNotCompleted && matchesRequestId;
  });

  // requestId disambiguates rows that share a type (e.g. tillage/fertilizer applications); callers with at most
  // one banner per type (crop, seeding, field-level) rely on the fallback to type instead.
  const commentFieldKey = requestId ?? type;
  const comment: string | undefined = Form.useWatch(["editingRequestComment", commentFieldKey], form);
  const trimmedComment = comment?.trim() ?? "";

  const submitComment = async () => {
    if (!onSubmitComment || !trimmedComment) return;

    setIsSubmittingComment(true);
    try {
      await onSubmitComment(trimmedComment);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const toggleCommentField = useCallback(() => {
    if (!isCommentFieldVisible) {
      setIsCommentFieldVisible(true);
    } else {
      // Clear the form field value when hiding
      const currentValues = form.getFieldsValue();
      form.setFieldsValue({
        ...currentValues,
        editingRequestComment: {
          ...currentValues.editingRequestComment,
          [commentFieldKey]: undefined,
        },
      });
      setIsCommentFieldVisible(false);
    }
  }, [isCommentFieldVisible, form, commentFieldKey]);

  if (isLoading || !activeEditingRequest) return null;

  return (
    <AlertBanner
      className="margin-top-100 margin-bottom-100"
      dataTestId="edit-request-banner"
      description={
        <>
          <Typography variant="paragraph-m" className="margin-top-0 margin-bottom-0">
            {activeEditingRequest.comment}
          </Typography>
          {isCommentFieldVisible && (
            <div style={{ animation: "fade-down 0.1s ease-in" }}>
              <TextArea
                dataTestId="edit-request-banner-textarea"
                name={["editingRequestComment", commentFieldKey]}
                className="margin-bottom-50"
                label={t("editingRequestBanner.commentLabel")}
                autoSize
              />
            </div>
          )}
        </>
      }
      link
      linkText={t(`editingRequestBanner.${isCommentFieldVisible ? "cancelButton" : "commentButton"}`)}
      linkAction={
        onSubmitComment &&
        isCommentFieldVisible && (
          <Button
            dataTestId="edit-request-banner-submit-button"
            type="primary"
            disabled={!trimmedComment}
            loading={isSubmittingComment}
            onClick={submitComment}
          >
            {t("editingRequestBanner.submitButton")}
          </Button>
        )
      }
      type="error"
      iconName="tabler/alert-triangle"
      iconAlign="start"
      onClick={toggleCommentField}
    />
  );
};
